import { useMemo, useState } from "react";
import { Tag } from "ui-kit-v2/src/tag/tag";
import { CoachReviewsDisabledPlayer, CoachReviewsPlayer } from "../Player";
import { CoachReviewsPlayerSortButton, sortPlayers, type PlayerSort } from "../PlayerSortButton";
import styles from "./styles.module.scss";
import { _ } from "i18n";
import { useGetReviews } from "common/src/gamePage/hooks/useGetReviews";
import { useStore as useGameStore } from "common/src/gamePage/store";

enum ReviewGroup {
	EligibleUnsent,
	EligibleSent,
	NotEligible,
}

const getReviewGroup = (isEligible: boolean, wasReviewSent: boolean): ReviewGroup => {
	if (isEligible === false) return ReviewGroup.NotEligible;

	return wasReviewSent ? ReviewGroup.EligibleSent : ReviewGroup.EligibleUnsent;
};

export const CoachReviewsPlayerList = () => {
	const { data, isLoading, isError } = useGetReviews();
	const gameData = useGameStore((state) => state.gameData);
	const canEditBadges = Boolean(gameData?.homeGameTeam.permissions.ratingBadgeEdit || gameData?.awayGameTeam.permissions.ratingBadgeEdit);

	const [sort, setSort] = useState<PlayerSort | null>(null);

	const players = data?.data.players;

	// The selected sort applies within each group, the groups keep their order
	const playersByReviewGroup = useMemo(
		() =>
			[...sortPlayers(players ?? [], sort)].sort(
				(playerA, playerB) =>
					getReviewGroup(playerA.reviewEligibility.isEligible, playerA.review !== null) -
					getReviewGroup(playerB.reviewEligibility.isEligible, playerB.review !== null)
			),
		[players, sort]
	);

	if (isLoading || isError) return null;

	return (
		<div className={styles["list"]}>
			<div className={styles["list__header"]}>
				<div className={styles["list__header-left"]}>
					<p className={styles["list__title"]}>{_("game.coachReviews.playerList.title")}</p>
					<Tag variant="secondary" palette="neutral">
						{_("game.coachReviews.playerList.count", { count: playersByReviewGroup.length })}
					</Tag>
				</div>
				<CoachReviewsPlayerSortButton value={sort} onChange={setSort} />
			</div>
			<div className={styles["list__body"]}>
				{playersByReviewGroup.map((player) =>
					player.reviewEligibility.error ? (
						<CoachReviewsDisabledPlayer
							key={player.gamePlayerId}
							eligibilityError={player.reviewEligibility.error}
							rating={player.rating}
							name={player.name || ""}
							jerseyNumber={player.numberSign}
							position={player.position}
						/>
					) : (
						<CoachReviewsPlayer
							key={player.gamePlayerId}
							playerId={player.gamePlayerId}
							rating={player.rating}
							name={player.name || ""}
							jerseyNumber={player.numberSign}
							position={player.position}
							hasParents={player.hasParent}
							sentReviewText={player.review?.text || null}
							sentEventIds={player.review?.events || null}
							aiGenerationAvailable={player.aiGenerationAvailable}
							canEditBadges={canEditBadges}
						/>
					)
				)}
			</div>
		</div>
	);
};
